/**
 * Next.js App Router searchParams can expose array-style query keys differently
 * (`conditions[]` vs `conditions`, string vs string[]). Normalize before calling the API.
 */
export type RawListingSearchParams = Record<string, string | string[] | undefined>;

/** Next.js may pass a single string or `string[]` for the same key. */
export function firstSearchParamString(
  sp: RawListingSearchParams,
  key: string,
): string | undefined {
  const v = sp[key];
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && v.length > 0) {
    const first = v[0];
    if (typeof first === 'string') return first;
  }
  return undefined;
}

function coerceStringList(v: string | string[] | undefined): string[] | undefined {
  if (v == null || v === '') return undefined;
  if (Array.isArray(v)) {
    const out = v.map(String).filter((s) => s.length > 0);
    return out.length ? out : undefined;
  }
  return [String(v)];
}

/** Parsed conditions for sidebar + API (New, Used, Refurbished). */
export function parseConditionsFromSearchParams(sp: RawListingSearchParams): string[] | undefined {
  const fromBracket = coerceStringList(sp['conditions[]']);
  if (fromBracket) return fromBracket;
  return coerceStringList(sp['conditions']);
}

export function parseBrandsFromSearchParams(sp: RawListingSearchParams): string[] | undefined {
  const fromBracket = coerceStringList(sp['brands[]']);
  if (fromBracket) return fromBracket;
  return coerceStringList(sp['brands']);
}

/** Minimum seller rating (4, 3, or 2 stars & up). */
export function parseMinRatingFromSearchParams(sp: RawListingSearchParams): string | undefined {
  const raw = firstSearchParamString(sp, 'min_rating');
  if (!raw) return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 2 || n > 5) return undefined;
  return String(Math.floor(n));
}

export { parseListingTypeFromSearchParams } from '@/lib/listingFilterOptions';

function firstList(sp: RawListingSearchParams, bracketKey: string, plainKey: string): string[] | undefined {
  const fromBracket = coerceStringList(sp[bracketKey]);
  if (fromBracket) return fromBracket;
  return coerceStringList(sp[plainKey]);
}

export function parseFuelTypeFromSearchParams(sp: RawListingSearchParams): string[] | undefined {
  return firstList(sp, 'fuel_type[]', 'fuel_type');
}

export function parseTransmissionFromSearchParams(sp: RawListingSearchParams): string[] | undefined {
  return firstList(sp, 'transmission[]', 'transmission');
}
